import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { LOCAL_LANGUAGE } from '@/constants/storageKey';
import {
  CODE_API_URL,
  BASIC_PAGE_URL,
  SEND_EMAIL_API_URL,
  footerAddressUrl,
  footerMenuUrl,
  footerContactUrl,
  footerSnsUrl,
  ipapiUrl,
} from '@/constants/apiUrls';
import {
  ARENA_EVENT_CATEGORY_KEY,
  ARENA_FILE_CATEGORY_KEY,
  ARENA_SEGMENT_CATEGORY_KEY,
  AUDIENCE_TYPE_KEY,
  BUSINESS_CATEGORY_KEY,
  FOOTER_ADDRESS_KEY,
  FOOTER_CONTACT_KEY,
  FOOTER_MENU_KEY,
  FOOTER_SNS_KEY,
  MEETING_EVENT_CATEGORY_KEY,
  RETRACTABLE_OPTIONS_KEY,
  SEGMENT_CATEGORY_KEY,
  STAGE_TYPE_KEY,
} from '@/constants/queryKey';
import { combineEnglishNameMenuTree } from '@/utils/modify-data/modifyData';

export function useGetBusinessCategory() {
  const language = localStorage.getItem(LOCAL_LANGUAGE);

  const getBusinessCategory = useCallback(async () => {
    const { status, data } = await axios.get(`${CODE_API_URL}/1?lang=${language}`);

    if (status === 200) {
      return data.data;
    }
  }, [language]);

  return useQuery({ queryKey: [BUSINESS_CATEGORY_KEY, language], queryFn: getBusinessCategory });
}

export function useGetSegmentCategory() {
  const language = localStorage.getItem(LOCAL_LANGUAGE);

  const getSegmentCategory = useCallback(async () => {
    const { status, data } = await axios.get(`${CODE_API_URL}/12?lang=${language}`);

    if (status === 200) {
      return data.data;
    }
  }, [language]);

  return useQuery({ queryKey: [SEGMENT_CATEGORY_KEY, language], queryFn: getSegmentCategory });
}

export function useGetArenaSegmentCategory() {
  const language = localStorage.getItem(LOCAL_LANGUAGE);

  const getArenaSegmentCategory = useCallback(async () => {
    const { status, data } = await axios.get(`${CODE_API_URL}/16?lang=${language}`);

    if (status === 200) {
      return data.data;
    }
  }, [language]);

  return useQuery({
    queryKey: [ARENA_SEGMENT_CATEGORY_KEY, language],
    queryFn: getArenaSegmentCategory,
  });
}

export function useArenaFileCategory() {
  const language = localStorage.getItem(LOCAL_LANGUAGE);

  const getArenaFileCategory = useCallback(async () => {
    const { status, data } = await axios.get(`${CODE_API_URL}/13?lang=${language}`);

    if (status === 200) {
      return data.data;
    }
  }, [language]);

  return useQuery({ queryKey: [ARENA_FILE_CATEGORY_KEY, language], queryFn: getArenaFileCategory });
}

export function useGetArenaEventCategory() {
  const language = localStorage.getItem(LOCAL_LANGUAGE);

  const getArenaEventCategory = useCallback(async () => {
    const { status, data } = await axios.get(`${CODE_API_URL}/2?lang=${language}`);

    if (status === 200) {
      return data.data;
    }
  }, [language]);

  return useQuery({
    queryKey: [ARENA_EVENT_CATEGORY_KEY, language],
    queryFn: getArenaEventCategory,
  });
}

export function useGetMeetingEventCategory() {
  const language = localStorage.getItem(LOCAL_LANGUAGE);

  const getMeetingEventCategory = useCallback(async () => {
    const { status, data } = await axios.get(`${CODE_API_URL}/11?lang=${language}`);

    if (status === 200) {
      return data.data;
    }
  }, [language]);

  return useQuery({
    queryKey: [MEETING_EVENT_CATEGORY_KEY, language],
    queryFn: getMeetingEventCategory,
  });
}

export function useGetStageType() {
  const language = localStorage.getItem(LOCAL_LANGUAGE);

  const getStageType = useCallback(async () => {
    const { status, data } = await axios.get(`${CODE_API_URL}/3?lang=${language}`);

    if (status === 200) {
      return data.data;
    }
  }, [language]);

  return useQuery({
    queryKey: [STAGE_TYPE_KEY, language],
    queryFn: getStageType,
  });
}

export function useGetRetractableOptions() {
  const language = localStorage.getItem(LOCAL_LANGUAGE);

  const getRetractableOptions = useCallback(async () => {
    const { status, data } = await axios.get(`${CODE_API_URL}/10?lang=${language}`);

    if (status === 200) {
      return data.data;
    }
  }, [language]);

  return useQuery({
    queryKey: [RETRACTABLE_OPTIONS_KEY, language],
    queryFn: getRetractableOptions,
  });
}

export function useGetAudienceType() {
  const language = localStorage.getItem(LOCAL_LANGUAGE);

  const getAudienceType = useCallback(async () => {
    const { status, data } = await axios.get(`${CODE_API_URL}/15?lang=${language}`);

    if (status === 200) {
      return data.data;
    }
  }, [language]);

  return useQuery({
    queryKey: [AUDIENCE_TYPE_KEY, language],
    queryFn: getAudienceType,
  });
}

export function useGetFooterQuery() {
  const language = localStorage.getItem(LOCAL_LANGUAGE);

  const { ipInformation } = useIpInformation();

  const getFooterAddress = useCallback(async () => {
    const { status, data } = await axios.get(`${footerAddressUrl}&language=${language}`);

    if (status === 200) {
      return data;
    }
  }, [language]);

  const getFooterMenu = useCallback(async () => {
    const { status: status1, data: data1 } = await axios.get(
      `${footerMenuUrl}&language=${language}&code=KR`
    );

    const { status: status2, data: data2 } = await axios.get(
      `${footerMenuUrl}&language=en&code=KR`
    );

    if (status1 === 200 && status2 === 200) {
      return combineEnglishNameMenuTree(data1.data, data2.data);
    }
  }, [language]);

  const getFooterContact = useCallback(async () => {
    const { status, data } = await axios.get(
      `${footerContactUrl}&language=${language}&code=${ipInformation?.countryCode}`
    );

    if (status === 200) {
      return data;
    }
  }, [language, ipInformation]);

  const getFooterSns = useCallback(async () => {
    const { status, data } = await axios.get(`${footerSnsUrl}&language=${language}`);

    if (status === 200) {
      return data;
    }
  }, [language]);

  const options = {
    enabled: !!language && !!ipInformation?.countryCode,
  };

  const results = useQueries({
    queries: [
      {
        queryKey: [FOOTER_ADDRESS_KEY, language],
        queryFn: async () => getFooterAddress(),
        ...options,
      },
      {
        queryKey: [FOOTER_MENU_KEY, language, ipInformation],
        queryFn: async () => getFooterMenu(),
        ...options,
      },
      {
        queryKey: [FOOTER_CONTACT_KEY, language],
        queryFn: async () => getFooterContact(),
        ...options,
      },
      {
        queryKey: [FOOTER_SNS_KEY, language],
        queryFn: async () => getFooterSns(),
        ...options,
      },
    ],
  });

  const footerAddress =
    results[0].data !== undefined && results[0].data !== null ? results[0].data[0] : [];
  const footerMenu =
    results[1].data !== undefined && results[1].data !== null ? results[1].data : [];
  const footerContact =
    results[2].data !== undefined && results[2].data !== null ? results[2].data[0] : [];
  const footerSns =
    results[3].data !== undefined && results[3].data !== null ? results[3].data[0] : [];

  const isLoading =
    results.some((result) => result.isLoading || result.isFetching) || !ipInformation?.countryCode;

  const isError = results.some((result) => result.isError);

  return {
    footerAddress,
    footerMenu,
    footerContact,
    footerSns,
    isLoading,
    isError,
  };
}

// export function useGetPrivacyPolicy() {
//   const language = localStorage.getItem(LOCAL_LANGUAGE);

//   const getPrivacyPolicy = useCallback(async () => {
//     return await axios.get(`${PRIVACY_POLICY_URL}?language=${language}`);
//   }, [language]);

//   return useSuspenseQuery({
//     queryKey: [PRIVACY_POLICY_KEY, language, localStorage],
//     queryFn: getPrivacyPolicy,
//     select: (data) => data.data[0],
//   });
// }

// export function useGetTermsConditions() {
//   const language = localStorage.getItem(LOCAL_LANGUAGE);

//   const getTermsConditions = useCallback(async () => {
//     return await axios.get(`${TERMS_CONDITIONS_URL}?language=${language}`);
//   }, [language]);

//   return useSuspenseQuery({
//     queryKey: [TERMS_CONDITIONS_KEY, language, localStorage],
//     queryFn: getTermsConditions,
//     select: (data) => data.data[0],
//   });
// }

export function usePostEmailForm() {
  const postEmailForm = useCallback(async (data) => {
    return await axios.post(SEND_EMAIL_API_URL, data);
  }, []);

  const mutation = useMutation({
    mutationFn: postEmailForm,
    onSuccess: (data) => {
      if (data.data.code === 200) {
        return true;
      }
    },
    onError: (error) => {
      console.log(error);
      return false;
    },
  });

  return {
    mutate: mutation.mutate,
    isSuccess: mutation.isSuccess,
    isPending: mutation.isPending,
    error: mutation.error,
  };
}

export function useGetBasicPage(alias, options = {}) {
  const language = localStorage.getItem(LOCAL_LANGUAGE);

  const getBasicPage = useCallback(async () => {
    const { status, data } = await axios.get(
      `${BASIC_PAGE_URL}?language=${language}&url_alias=/${alias}`
    );

    if (status === 200) {
      return data[0];
    }
  }, [language, alias]);

  return useQuery({
    queryKey: ['basicPageDetails', language, alias],
    queryFn: getBasicPage,
    ...options,
  });
}

export function useIpInformation() {
  const navigate = useNavigate();

  const lang = localStorage.getItem(LOCAL_LANGUAGE);
  const getIpInformation = useCallback(async () => {
    try {
      const { status, data } = await axios.get(
        `${ipapiUrl}?key=${process.env.REACT_APP_IPAPI_KEY}`
      );

      if (status === 200) {
        return data;
      }
    } catch (error) {
      navigate(`/${lang}/maintenance`);
    }
  }, [lang, navigate]);

  const { data: ipInformation } = useQuery({
    queryKey: ['ipInformation'],
    queryFn: getIpInformation,
    retry: 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    refetchInterval: 60 * 1000 * 180,
    refetchIntervalInBackground: true,
    staleTime: Infinity,
    gcTime: Infinity, // v5: cacheTime -> gcTime로 이름이 바뀌었습니다
  });

  return { ipInformation };
}
